/**
 * Compose dialog — new messages and replies.
 *
 * Plain-text composition. Sends go through the session; a transport-level
 * failure queues the message in the outbox (stated in the dialog, never
 * hidden), while a mail-host rejection stays on the form for fixing.
 * Drafts persist locally per compose context and survive reloads.
 */
import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { deleteDraft, readDraft, saveDraft } from "../state/cache.ts";
import { deliverOutbox, replyHeadersFor, sendMail } from "../state/session.ts";
import {
  activeAccountId,
  activeMessage,
  composeRequest,
  sending,
} from "../state/store.ts";

import { CloseIcon } from "./icons.tsx";

/**
 * Fields of the compose form.
 */
interface ComposeFields {
  /**
  Recipients, comma-separated as typed.
   */
  readonly to: string;
  /**
  Copies, comma-separated as typed.
   */
  readonly cc: string;
  /**
  Subject line.
   */
  readonly subject: string;
  /**
  Body text.
   */
  readonly text: string;
}

/**
 * The empty form.
 *
 * @returns Empty fields.
 */
function emptyFields(): ComposeFields {
  return { to: "", cc: "", subject: "", text: "" };
}

/**
 * Renders the composer as a modal dialog.
 *
 * @returns The dialog element, or `null` when no compose was requested.
 */
export function ComposeDialog(): JSX.Element | null {
  const request = composeRequest.value;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fields, setFields] = useState<ComposeFields>(emptyFields);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    if (request !== null && !dialog.open) {
      dialog.showModal();
    } else if (request === null && dialog.open) {
      dialog.close();
    }
  }, [request]);

  useEffect(() => {
    if (request === null) {
      return;
    }
    const accountId = activeAccountId.value;
    const draftKey = request.mode === "reply" ? `reply-${request.uid}` : "new";
    setError(null);
    setNote(null);
    void (async () => {
      const draft =
        accountId === null ? null : await readDraft(accountId, draftKey);
      if (draft !== null) {
        setFields({
          to: draft.to,
          cc: draft.cc,
          subject: draft.subject,
          text: draft.text,
        });
        setNote("Draft restored from this device.");
        return;
      }
      if (request.mode === "reply") {
        const message = activeMessage.value;
        const peer = (message?.reply_to[0] ?? message?.from[0])?.address ?? "";
        setFields({
          to: peer,
          cc: "",
          subject:
            message?.subject === null || message?.subject === undefined
              ? ""
              : `Re: ${message.subject}`,
          text: "",
        });
        return;
      }
      setFields(emptyFields());
    })();
  }, [request]);

  if (request === null) {
    return null;
  }

  const submit = (): void => {
    if (activeAccountId.value === null) {
      setError("Connect an account before sending.");
      return;
    }
    const recipients = splitAddresses(fields.to);
    if (recipients.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    if (fields.text.trim() === "") {
      setError("Write a message before sending.");
      return;
    }
    const copies = splitAddresses(fields.cc);
    const reply = request.mode === "reply" ? replyHeadersFor(request.uid) : {};
    setError(null);
    void (async () => {
      try {
        const outcome = await sendMail({
          to: recipients,
          ...(copies.length > 0 && { cc: copies }),
          ...(fields.subject.trim() !== "" && {
            subject: fields.subject.trim(),
          }),
          text: fields.text,
          ...reply,
        });
        // The shell's outbox strip reports a queued send; the dialog's job
        // is done either way.
        await deleteDraft(activeAccountId.value ?? "", draftKeyOf(request));
        composeRequest.value = null;
        setFields(emptyFields());
        setNote(null);
        if (outcome === "queued") {
          void deliverOutbox();
        }
      } catch (error_: unknown) {
        setError(
          error_ instanceof Error
            ? error_.message
            : "The message could not be sent.",
        );
      }
    })();
  };

  const keepDraft = (): void => {
    const accountId = activeAccountId.value;
    if (accountId === null) {
      return;
    }
    void (async () => {
      await saveDraft({
        accountId,
        draftKey: draftKeyOf(request),
        ...fields,
        savedAt: Date.now(),
      });
      setNote("Draft saved on this device.");
    })();
  };

  return (
    <dialog
      class="compose-dialog"
      aria-labelledby="compose-title"
      ref={dialogRef}
      onClose={() => {
        composeRequest.value = null;
      }}
    >
      <div class="dialog-header">
        <h2 id="compose-title">
          {request.mode === "reply" ? "Reply" : "New message"}
        </h2>
        <button
          type="button"
          class="icon-button"
          onClick={() => {
            composeRequest.value = null;
          }}
        >
          <CloseIcon title="Close composer" />
        </button>
      </div>
      <form
        class="compose-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label class="field">
          <span>To</span>
          <input
            name="to"
            type="text"
            placeholder="peer@example.com, other@example.net"
            autoComplete="off"
            value={fields.to}
            onInput={(event) => {
              setFields({ ...fields, to: event.currentTarget.value });
            }}
          />
        </label>
        <label class="field">
          <span>Cc</span>
          <input
            name="cc"
            type="text"
            placeholder="optional"
            autoComplete="off"
            value={fields.cc}
            onInput={(event) => {
              setFields({ ...fields, cc: event.currentTarget.value });
            }}
          />
        </label>
        <label class="field">
          <span>Subject</span>
          <input
            name="subject"
            type="text"
            placeholder="Subject"
            autoComplete="off"
            value={fields.subject}
            onInput={(event) => {
              setFields({ ...fields, subject: event.currentTarget.value });
            }}
          />
        </label>
        <label class="field">
          <span>Message</span>
          <textarea
            name="text"
            rows={10}
            required
            value={fields.text}
            onInput={(event) => {
              setFields({ ...fields, text: event.currentTarget.value });
            }}
          />
        </label>
        <div class="form-actions">
          <button
            type="button"
            class="ghost-button"
            onClick={keepDraft}
            disabled={sending.value}
          >
            Save draft
          </button>
          {/* The button stays enabled so the validation copy can explain
              what is missing; a disabled control teaches nothing. */}
          <button type="submit" class="primary-button" disabled={sending.value}>
            {sending.value ? "Sending…" : "Send"}
          </button>
        </div>
        {error === null ? null : (
          <p class="form-error" role="alert">
            {error}
          </p>
        )}
        {note === null ? null : (
          <p class="settings-note" role="status">
            {note}
          </p>
        )}
      </form>
    </dialog>
  );
}

/**
 * The storage key of a compose request.
 *
 * @param request - The pending compose request (non-null at call sites).
 * @returns The draft key.
 */
function draftKeyOf(request: NonNullable<typeof composeRequest.value>): string {
  return request.mode === "reply" ? `reply-${request.uid}` : "new";
}

/**
 * Splits and sanity-checks typed addresses.
 *
 * @param text - Comma-separated recipients as typed.
 * @returns The entries that look like addresses.
 */
function splitAddresses(text: string): string[] {
  return text
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) =>
      /^[^\s,;<>@]+@[^\s,;<>@][^\s,.;<>@]*\.[^\s,;<>@]+$/u.test(entry),
    );
}
